import type { PartnerBusiness, PartnerDetail } from '@pulsify/shared/dto'
import { Link } from '@tanstack/react-router'
import { ArrowLeft, Download, Pencil, Search } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useAssignBusinessPartner, usePartnerDetail } from '~/entities/partner/model/use-partners'
import { useAdminBusinesses } from '~/entities/session/model/use-admin-auth'
import { planName } from '~/entities/subscription/plans'
import { partnerBusinessesCsv } from '~/features/admin/partner-csv'
import { PartnerFormDialog } from '~/features/admin/partner-form-dialog'
import { PartnerLink, partnerKindLabel } from '~/features/admin/partner-link'
import * as m from '~/paraglide/messages'
import { getLocale } from '~/paraglide/runtime.js'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '~/shared/ui/alert-dialog'
import { Badge } from '~/shared/ui/badge'
import { Button } from '~/shared/ui/button'
import { Card, CardContent } from '~/shared/ui/card'
import { Input } from '~/shared/ui/input'
import { Skeleton } from '~/shared/ui/skeleton'

const ASSIGN_RESULTS = 6

function useCount() {
  const format = new Intl.NumberFormat(getLocale())
  return (value: number) => format.format(value)
}

function StatTile({ label, value, hint }: { label: string; value: number; hint?: string }) {
  const count = useCount()
  return (
    <Card className="flex flex-col gap-1 p-4" title={hint}>
      <span className="text-muted-foreground text-xs">{label}</span>
      <span className="font-semibold text-2xl tabular-nums">{count(value)}</span>
    </Card>
  )
}

function AssignBusiness({ detail }: { detail: PartnerDetail }) {
  const [search, setSearch] = useState('')
  const businesses = useAdminBusinesses()
  const assign = useAssignBusinessPartner()

  const matches = useMemo(() => {
    const term = search.trim().toLocaleLowerCase()
    if (!term) return []
    const assigned = new Set(detail.businesses.map((business) => business.id))
    return (businesses.data ?? [])
      .filter(
        (business) =>
          !assigned.has(business.id) && business.legalName.toLocaleLowerCase().includes(term),
      )
      .slice(0, ASSIGN_RESULTS)
  }, [businesses.data, detail.businesses, search])

  return (
    <Card className="flex flex-col gap-3 p-5">
      <div className="space-y-1">
        <h2 className="font-medium text-sm">{m.partnersAssignTitle()}</h2>
        <p className="text-muted-foreground text-xs">{m.partnersAssignHelp()}</p>
      </div>
      <div className="relative sm:max-w-md">
        <Search
          className="top-1/2 left-3 absolute w-4 h-4 text-muted-foreground -translate-y-1/2"
          aria-hidden
        />
        <Input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={m.partnersAssignSearch()}
          aria-label={m.partnersAssignSearch()}
          className="pl-9"
        />
      </div>
      {search.trim() === '' ? null : matches.length === 0 ? (
        <p className="text-muted-foreground text-sm">{m.partnersAssignNoMatch()}</p>
      ) : (
        <ul className="divide-y divide-border rounded-md border sm:max-w-md">
          {matches.map((business) => (
            <li key={business.id} className="flex items-center justify-between gap-3 px-3 py-2">
              <span className="min-w-0 truncate text-sm">{business.legalName}</span>
              <Button
                size="sm"
                variant="outline"
                disabled={assign.isPending}
                onClick={() =>
                  assign.mutate(
                    { businessId: business.id, partnerId: detail.partner.id },
                    { onSuccess: () => setSearch('') },
                  )
                }
              >
                {m.partnersAssignAction()}
              </Button>
            </li>
          ))}
        </ul>
      )}
    </Card>
  )
}

function RemoveButton({ business }: { business: PartnerBusiness }) {
  const [open, setOpen] = useState(false)
  const assign = useAssignBusinessPartner()
  return (
    <>
      <Button variant="ghost" size="sm" disabled={assign.isPending} onClick={() => setOpen(true)}>
        {m.partnersRemove()}
      </Button>
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {m.partnersRemoveTitle({ name: business.companyName })}
            </AlertDialogTitle>
            <AlertDialogDescription>{m.partnersRemoveBody()}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{m.cancel()}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => assign.mutate({ businessId: business.id, partnerId: null })}
            >
              {m.partnersRemove()}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}

function BusinessRow({ business }: { business: PartnerBusiness }) {
  const locale = getLocale()
  const count = useCount()
  const date = (value: string | null) =>
    value ? new Date(value).toLocaleDateString(locale, { dateStyle: 'medium' }) : '–'

  return (
    <tr className="border-border border-b last:border-0">
      <td className="py-3 pr-4 font-medium">{business.companyName}</td>
      <td className="py-3 pr-4 text-muted-foreground whitespace-nowrap">
        {date(business.signedUpAt)}
      </td>
      <td className="py-3 pr-4 text-muted-foreground whitespace-nowrap">
        {date(business.referredAt)}
      </td>
      <td className="py-3 pr-4">
        <Badge variant={business.paid ? 'default' : 'outline'} className="text-xs">
          {planName(business.subscriptionTier)}
        </Badge>
      </td>
      <td className="py-3 pr-4">{business.active ? m.partnersYes() : m.partnersNo()}</td>
      <td className="py-3 pr-4 tabular-nums">{count(business.lunchMenus)}</td>
      <td className="py-3 pr-4 tabular-nums">{count(business.menuScans)}</td>
      <td className="py-3 pr-4 tabular-nums">{count(business.eventsPublished)}</td>
      <td className="py-3 pr-4 text-right">
        <RemoveButton business={business} />
      </td>
    </tr>
  )
}

function downloadCsv(detail: PartnerDetail) {
  const csv = partnerBusinessesCsv(detail.businesses, {
    business: m.partnersColumnBusiness(),
    signedUp: m.partnersColumnSignedUp(),
    assigned: m.partnersColumnAssigned(),
    plan: m.partnersColumnPlan(),
    active: m.partnersColumnActive(),
    lunchMenus: m.partnersColumnLunchMenus(),
    menuScans: m.partnersColumnMenuScans(),
    events: m.partnersColumnEvents(),
    yes: m.partnersYes(),
    no: m.partnersNo(),
  })
  const url = URL.createObjectURL(new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8' }))
  const link = document.createElement('a')
  link.href = url
  link.download = `partner-${detail.partner.code}.csv`
  link.click()
  URL.revokeObjectURL(url)
}

function PartnerDetailView({ detail }: { detail: PartnerDetail }) {
  const { partner, businesses } = detail
  const { stats } = partner

  return (
    <>
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div className="space-y-2 min-w-0">
          <div className="flex flex-wrap items-center gap-2.5">
            <h1 className="font-semibold text-foreground text-3xl tracking-tight">
              {partner.name}
            </h1>
            <Badge variant="outline">{partnerKindLabel(partner.kind)}</Badge>
          </div>
          {partner.contactEmail ? (
            <a
              href={`mailto:${partner.contactEmail}`}
              className="text-muted-foreground text-sm hover:underline"
            >
              {partner.contactEmail}
            </a>
          ) : null}
          {partner.note ? (
            <p className="max-w-3xl text-muted-foreground text-sm whitespace-pre-line">
              {partner.note}
            </p>
          ) : null}
          <PartnerLink partner={partner} />
        </div>
        <PartnerFormDialog
          partner={partner}
          trigger={
            <Button variant="outline">
              <Pencil className="mr-2 h-4 w-4" />
              {m.partnersEdit()}
            </Button>
          }
        />
      </header>

      <div className="gap-3 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6">
        <StatTile label={m.partnersColumnBusinesses()} value={stats.businesses} />
        <StatTile
          label={m.partnersColumnActive()}
          value={stats.activeBusinesses}
          hint={m.partnersStatActiveHint()}
        />
        <StatTile label={m.partnersColumnLunchMenus()} value={stats.lunchMenus} />
        <StatTile label={m.partnersColumnMenuScans()} value={stats.menuScans} />
        <StatTile label={m.partnersColumnEvents()} value={stats.eventsPublished} />
        <StatTile label={m.partnersColumnPaid()} value={stats.paidBusinesses} />
      </div>

      <section className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="font-semibold text-lg">{m.partnersBusinessesTitle()}</h2>
          {businesses.length > 0 ? (
            <Button variant="outline" size="sm" onClick={() => downloadCsv(detail)}>
              <Download className="mr-2 h-4 w-4" />
              {m.partnersExportCsv()}
            </Button>
          ) : null}
        </div>
        <Card>
          <CardContent className="p-0 overflow-x-auto">
            {businesses.length === 0 ? (
              <p className="p-8 text-muted-foreground text-sm text-center">
                {m.partnersBusinessesEmpty()}
              </p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-border border-b text-muted-foreground text-xs text-left uppercase tracking-wide">
                    <th className="py-3 pr-4 pl-4 font-medium">{m.partnersColumnBusiness()}</th>
                    <th className="py-3 pr-4 font-medium">{m.partnersColumnSignedUp()}</th>
                    <th className="py-3 pr-4 font-medium">{m.partnersColumnAssigned()}</th>
                    <th className="py-3 pr-4 font-medium">{m.partnersColumnPlan()}</th>
                    <th className="py-3 pr-4 font-medium" title={m.partnersStatActiveHint()}>
                      {m.partnersColumnActive()}
                    </th>
                    <th className="py-3 pr-4 font-medium">{m.partnersColumnLunchMenus()}</th>
                    <th className="py-3 pr-4 font-medium">{m.partnersColumnMenuScans()}</th>
                    <th className="py-3 pr-4 font-medium">{m.partnersColumnEvents()}</th>
                    <th className="py-3 pr-4 font-medium">
                      <span className="sr-only">{m.partnersRemove()}</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="[&_td:first-child]:pl-4">
                  {businesses.map((business) => (
                    <BusinessRow key={business.id} business={business} />
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      </section>

      <AssignBusiness detail={detail} />
    </>
  )
}

export function PartnerDetailPage({ partnerId }: { partnerId: string }) {
  const query = usePartnerDetail(partnerId)

  return (
    <div className="px-4 sm:px-8 py-8 sm:py-16">
      <div className="space-y-8 mx-auto max-w-6xl">
        <Link
          to="/admin/partners"
          className="inline-flex items-center gap-1.5 text-muted-foreground text-sm hover:text-foreground"
        >
          <ArrowLeft className="w-4 h-4" aria-hidden />
          {m.partnersBack()}
        </Link>
        {query.isLoading ? (
          <div className="space-y-3">
            {['a', 'b', 'c'].map((placeholder) => (
              <Skeleton key={placeholder} className="w-full h-12" />
            ))}
          </div>
        ) : query.data ? (
          <PartnerDetailView detail={query.data} />
        ) : (
          <p className="text-muted-foreground text-sm">{m.partnersNotFound()}</p>
        )}
      </div>
    </div>
  )
}
